import { FocusRow, type RowState } from './V2FocusRow';
import type { V2Response, V2ResponseOption } from '../api/v2TakeApi';

/**
 * One readiness question of an MVP 2 pillar.
 *
 * The row's chrome — chip, header, collapsing panel — is FocusRow, shared with
 * Discovery. What is here is what a readiness question answers with: four fixed
 * tiles, each carrying what the answer means.
 */

export type { RowState };

function OptionTile({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: V2ResponseOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      // Also disabled while the row is closed: the tiles stay mounted so the
      // row can animate, and a disabled button is out of the tab order, which
      // keeps a closed question unreachable by keyboard.
      disabled={disabled}
      className={`rounded-[10px] border px-[13px] py-[15px] text-left transition-colors duration-150 motion-reduce:transition-none ${
        selected
          ? 'border-[1.5px] border-[#026857] bg-[#026857]/10'
          : 'border-gray-200 bg-white hover:border-[#026857] disabled:hover:border-gray-200'
      }`}
    >
      <span className="block text-[14.5px] font-semibold text-gray-900">{option.label}</span>
      <span className="mt-0.5 block text-[12.5px] leading-snug text-gray-500">{option.help}</span>
    </button>
  );
}

export function QuestionRow({
  question,
  position,
  total,
  state,
  responseOptions,
  answer,
  answerLabel,
  isSaving,
  onSelect,
  onChange,
  rowRef,
}: {
  question: string;
  position: number;
  total: number;
  state: RowState;
  responseOptions: V2ResponseOption[];
  answer: V2Response | undefined;
  answerLabel: string | undefined;
  isSaving: boolean;
  onSelect: (value: V2Response) => void;
  onChange: () => void;
  rowRef: React.RefObject<HTMLDivElement | null> | undefined;
}) {
  const open = state === 'open';

  return (
    <FocusRow
      question={question}
      position={position}
      total={total}
      state={state}
      answerSummary={answerLabel}
      isBusy={isSaving}
      onReopen={onChange}
      rowRef={rowRef}
    >
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {responseOptions.map((option) => (
          <OptionTile
            key={option.value}
            option={option}
            selected={answer === option.value}
            disabled={!open || isSaving}
            onSelect={() => onSelect(option.value)}
          />
        ))}
      </div>
    </FocusRow>
  );
}
